import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { clsx } from "clsx";
import { useOwnerSettingsMutation } from "@features/settings/useOwnerSettings";
import { SUPPORTED_LANGUAGES } from "@lib/i18n/languages";
import { useLanguageStore } from "@stores/language";

/** Quick language switch. Applies at once and saves the choice to the account. */
export const LanguageMenu: React.FC = () => {
    const { t } = useTranslation();
    const language = useLanguageStore((s) => s.language);
    const setLanguage = useLanguageStore((s) => s.setLanguage);
    const save = useOwnerSettingsMutation();
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const choose = (code: (typeof SUPPORTED_LANGUAGES)[number]["code"]) => {
        setOpen(false);
        if (code === language) return;
        setLanguage(code);
        save.mutate({ language: code });
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label={t("settings.languageMenu.label")}
                aria-haspopup="menu"
                aria-expanded={open}
                className="flex items-center gap-1 rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
            >
                <Globe size={20} className="text-neutral-dark dark:text-neutral-light" />
                <span className="hidden text-xs font-semibold uppercase text-neutral-dark dark:text-neutral-light sm:inline">
                    {language.split("-")[0]}
                </span>
            </button>
            {open && (
                <ul
                    role="menu"
                    className="absolute end-0 top-full z-[60] mt-3 w-48 overflow-hidden rounded-xl border border-neutral-border/50 bg-white/95 py-1 shadow-xl backdrop-blur-xl dark:border-neutral-border/20 dark:bg-neutral-dark/95"
                >
                    {SUPPORTED_LANGUAGES.map((l) => (
                        <li key={l.code} role="none">
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={l.code === language}
                                onClick={() => choose(l.code)}
                                className={clsx(
                                    "flex w-full items-center justify-between gap-3 px-4 py-2.5 text-start text-sm transition-colors hover:bg-neutral-light/70 dark:hover:bg-white/10",
                                    l.code === language
                                        ? "font-semibold text-primary dark:text-gold"
                                        : "text-neutral-dark dark:text-neutral-light"
                                )}
                            >
                                <span lang={l.code}>{l.label}</span>
                                {l.code === language && <Check size={16} aria-hidden />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
